/**
 * Navigation Rule Implementations
 *
 * Concrete rule classes that can be composed to create complex visibility logic.
 * Each rule encapsulates a specific check (role, permission, org context, etc.).
 */

import type { NavigationRule, NavigationContext } from './NavigationRule';

/**
 * Rule that always makes item visible.
 * Use for items that should always appear in navigation.
 */
export class AlwaysVisibleRule implements NavigationRule {
  isVisible(): boolean {
    return true;
  }

  isClickable(): boolean {
    return true;
  }

  isEnabled(): boolean {
    return true;
  }
}

/**
 * Rule that requires user to have ANY of the specified permissions.
 * Uses OR logic - user needs at least one permission.
 */
export class RequiresPermissionRule implements NavigationRule {
  constructor(private permissions: string[]) {}

  isVisible(context: NavigationContext): boolean {
    return this.permissions.some((perm) => context.hasPermission(perm));
  }
}

/**
 * Rule that requires user to have ALL of the specified permissions.
 * Uses AND logic - user needs all permissions.
 */
export class RequiresAllPermissionsRule implements NavigationRule {
  constructor(private permissions: string[]) {}

  isVisible(context: NavigationContext): boolean {
    return this.permissions.every((perm) => context.hasPermission(perm));
  }
}

/**
 * Rule that requires an organization to be selected.
 * Hides items when user has no organization context.
 */
export class RequiresOrgRule implements NavigationRule {
  isVisible(context: NavigationContext): boolean {
    return context.currentOrg !== null;
  }
}

/**
 * Rule that only shows item when NO organization is selected.
 * Useful for "Create Organization" or "My Organizations" pages.
 */
export class RequiresNoOrgRule implements NavigationRule {
  isVisible(context: NavigationContext): boolean {
    return context.currentOrg === null;
  }
}

/**
 * Rule that hides item from managed (child) organizations.
 * A managed org can't act as a manager/partner, so manager-only surfaces
 * (e.g. the Partner Program) should not appear for it.
 */
export class RequiresNotManagedRule implements NavigationRule {
  isVisible(context: NavigationContext): boolean {
    return !context.isManaged;
  }
}

/**
 * Rule that requires user to be a system administrator.
 */
export class AdminOnlyRule implements NavigationRule {
  isVisible(context: NavigationContext): boolean {
    return context.isSystemAdmin();
  }
}

/**
 * Rule that hides item from system administrators.
 * Useful for items that only regular users should see.
 */
export class HideFromAdminRule implements NavigationRule {
  isVisible(context: NavigationContext): boolean {
    return !context.isSystemAdmin();
  }
}

/**
 * Rule that requires user to be authenticated.
 * Usually redundant (nav only shows when authenticated) but available for completeness.
 */
export class RequiresAuthRule implements NavigationRule {
  isVisible(context: NavigationContext): boolean {
    return context.user !== null;
  }
}

/**
 * Composite rule that combines multiple rules with AND/OR logic.
 *
 * @example AND logic (all rules must pass):
 * new CompositeRule([
 *   new RequiresOrgRule(),
 *   new RequiresPermissionRule(['organizations:update'])
 * ], 'AND')
 *
 * @example OR logic (any rule can pass):
 * new CompositeRule([
 *   new AdminOnlyRule(),
 *   new RequiresPermissionRule(['organizations:billing'])
 * ], 'OR')
 */
export class CompositeRule implements NavigationRule {
  constructor(
    private rules: NavigationRule[],
    private operator: 'AND' | 'OR' = 'AND',
  ) {}

  isVisible(context: NavigationContext): boolean {
    if (this.rules.length === 0) {
      return true;
    }

    if (this.operator === 'AND') {
      return this.rules.every((rule) => rule.isVisible(context));
    }
    return this.rules.some((rule) => rule.isVisible(context));
  }

  isClickable(context: NavigationContext): boolean {
    if (this.rules.length === 0) {
      return true;
    }

    if (this.operator === 'AND') {
      return this.rules.every((rule) => rule.isClickable?.(context) ?? true);
    }
    return this.rules.some((rule) => rule.isClickable?.(context) ?? true);
  }

  isEnabled(context: NavigationContext): boolean {
    if (this.rules.length === 0) {
      return true;
    }

    if (this.operator === 'AND') {
      return this.rules.every((rule) => rule.isEnabled?.(context) ?? true);
    }
    return this.rules.some((rule) => rule.isEnabled?.(context) ?? true);
  }
}

/**
 * Custom rule that takes a function for visibility check.
 * Allows inline logic without creating a new rule class.
 *
 * @example
 * new CustomRule(ctx => ctx.user?.email.endsWith('@admin.com'))
 */
export class CustomRule implements NavigationRule {
  constructor(
    private visibilityFn: (context: NavigationContext) => boolean,
    private clickableFn?: (context: NavigationContext) => boolean,
    private enabledFn?: (context: NavigationContext) => boolean,
  ) {}

  isVisible(context: NavigationContext): boolean {
    return this.visibilityFn(context);
  }

  isClickable(context: NavigationContext): boolean {
    return this.clickableFn?.(context) ?? true;
  }

  isEnabled(context: NavigationContext): boolean {
    return this.enabledFn?.(context) ?? true;
  }
}

/**
 * Rule that requires an active or trialing subscription.
 * Hides items when the organization has no valid subscription.
 */
export class RequiresActiveSubscriptionRule implements NavigationRule {
  isVisible(context: NavigationContext): boolean {
    return context.hasActiveSubscription();
  }
}

/**
 * Rule that requires a specific feature flag from the subscription tier.
 * Feature keys correspond to the tier's features object (e.g., 'hasWebhooks').
 *
 * @example
 * new RequiresFeatureRule('hasAuditLogs')
 */
export class RequiresFeatureRule implements NavigationRule {
  constructor(private featureKey: string) {}

  isVisible(context: NavigationContext): boolean {
    return context.hasFeature(this.featureKey);
  }
}

// Re-export types for convenience
export type { NavigationRule, NavigationContext } from './NavigationRule';
